import React, { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';

interface AggregateRow {
  function: string;
  column: string;
  alias: string;
}

export interface AggregationBuilderModalProps {
  open: boolean;
  onClose: () => void;
  /** Available target tables. */
  targetTables: string[];
  /** Available columns for aggregation. */
  availableColumns: string[];
  /** Pre-filled values. */
  initialTargetTable?: string;
  initialGroupBy?: string[];
  initialAggregates?: AggregateRow[];
  initialHaving?: string;
  /** Submit handler. */
  onSubmit: (data: {
    targetTable: string;
    groupBy: string[];
    aggregates: AggregateRow[];
    having?: string;
  }) => void;
}

const AGG_FUNCTIONS = ['SUM', 'COUNT', 'AVG', 'MIN', 'MAX', 'COUNT DISTINCT', 'LIST', 'STRING_AGG'];

/**
 * Phase 4.4: Visual Aggregation Builder Modal.
 * Configures GROUP BY, aggregate functions, and optional HAVING clause.
 */
const AggregationBuilderModal: React.FC<AggregationBuilderModalProps> = ({
  open,
  onClose,
  targetTables,
  availableColumns,
  initialTargetTable = '',
  initialGroupBy = [],
  initialAggregates = [{ function: 'SUM', column: '', alias: '' }],
  initialHaving = '',
  onSubmit,
}) => {
  const [targetTable, setTargetTable] = useState(initialTargetTable);
  const [groupBy, setGroupBy] = useState<string[]>(initialGroupBy);
  const [aggregates, setAggregates] = useState<AggregateRow[]>(
    initialAggregates.length > 0 ? initialAggregates : [{ function: 'SUM', column: '', alias: '' }]
  );
  const [having, setHaving] = useState(initialHaving);

  const handleAddAggregate = () => setAggregates([...aggregates, { function: 'SUM', column: '', alias: '' }]);
  const handleRemoveAggregate = (idx: number) => setAggregates(aggregates.filter((_, i) => i !== idx));
  const handleAggChange = (idx: number, field: keyof AggregateRow, value: string) => {
    const updated = [...aggregates];
    updated[idx] = { ...updated[idx], [field]: value };
    setAggregates(updated);
  };

  const isValid = targetTable && groupBy.length > 0 && aggregates.some(a => a.column);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6" sx={{ fontWeight: 700, fontSize: 16 }}>
          Aggregation Builder
        </Typography>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          {/* Target Table */}
          <FormControl fullWidth size="small">
            <InputLabel>Target Table</InputLabel>
            <Select value={targetTable} label="Target Table" onChange={(e) => setTargetTable(e.target.value)}>
              {targetTables.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </Select>
          </FormControl>

          {/* GROUP BY */}
          <Box>
            <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', mb: 0.5, display: 'block' }}>
              GROUP BY
            </Typography>
            <FormControl fullWidth size="small">
              <Select
                multiple
                value={groupBy}
                onChange={(e) => setGroupBy(typeof e.target.value === 'string' ? [e.target.value] : e.target.value)}
                renderValue={(selected) => (
                  <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map(v => <Chip key={v} label={v} size="small" sx={{ height: 22, fontSize: 11 }} />)}
                  </Stack>
                )}
                sx={{ fontSize: 12 }}
              >
                {availableColumns.map(c => <MenuItem key={c} value={c} sx={{ fontSize: 12 }}>{c}</MenuItem>)}
              </Select>
            </FormControl>
          </Box>

          {/* Aggregate Functions */}
          <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                Aggregate Functions
              </Typography>
              <Button size="small" startIcon={<AddIcon />} onClick={handleAddAggregate} sx={{ fontSize: 11, textTransform: 'none' }}>
                Add
              </Button>
            </Stack>
            <Stack spacing={1}>
              {aggregates.map((agg, idx) => (
                <Stack key={idx} direction="row" spacing={1} alignItems="center">
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <Select
                      value={agg.function}
                      onChange={(e) => handleAggChange(idx, 'function', e.target.value)}
                      sx={{ fontSize: 12 }}
                    >
                      {AGG_FUNCTIONS.map(f => <MenuItem key={f} value={f} sx={{ fontSize: 12 }}>{f}</MenuItem>)}
                    </Select>
                  </FormControl>
                  <FormControl fullWidth size="small">
                    <Select
                      displayEmpty
                      value={agg.column}
                      onChange={(e) => handleAggChange(idx, 'column', e.target.value)}
                      sx={{ fontSize: 12 }}
                    >
                      <MenuItem value="" disabled><em>Column</em></MenuItem>
                      {availableColumns.map(c => <MenuItem key={c} value={c} sx={{ fontSize: 12 }}>{c}</MenuItem>)}
                    </Select>
                  </FormControl>
                  <TextField
                    size="small"
                    placeholder="Alias"
                    value={agg.alias}
                    onChange={(e) => handleAggChange(idx, 'alias', e.target.value)}
                    sx={{ minWidth: 100, '& .MuiInputBase-input': { fontSize: 12 } }}
                  />
                  {aggregates.length > 1 && (
                    <IconButton size="small" onClick={() => handleRemoveAggregate(idx)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                </Stack>
              ))}
            </Stack>
          </Box>

          {/* HAVING Clause */}
          <TextField
            size="small"
            label="HAVING Clause (optional)"
            placeholder="e.g. COUNT(*) > 10"
            value={having}
            onChange={(e) => setHaving(e.target.value)}
            fullWidth
            multiline
            minRows={2}
            InputProps={{ sx: { fontFamily: 'monospace', fontSize: 12 } }}
          />
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button onClick={onClose} sx={{ fontSize: 12, textTransform: 'none' }}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!isValid}
          onClick={() => {
            onSubmit({
              targetTable,
              groupBy,
              aggregates: aggregates.filter(a => a.column),
              having: having || undefined,
            });
            onClose();
          }}
          sx={{ fontSize: 12, textTransform: 'none' }}
        >
          Apply Aggregation
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AggregationBuilderModal;
